import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = { activeTab: { type: String, default: "timeline" } }

  connect() {
    // Restore the last viewed tab for this page from localStorage so
    // that after a destroy/edit/etc. roundtrip the practitioner lands
    // back on whichever tab they were working in. Falls back to the
    // activeTabValue default if nothing's stored or the stored tab
    // no longer exists on the page.
    const remembered = this._readRemembered()
    const initial = (remembered && this._isValidTab(remembered)) ? remembered : this.activeTabValue
    this.activeTabValue = initial
    this.showTab(initial)
    this._onKeydown = this.handleKeydown.bind(this)
    this.tabTargets.forEach(tab => tab.addEventListener("keydown", this._onKeydown))
  }

  disconnect() {
    if (this._onKeydown) {
      this.tabTargets.forEach(tab => tab.removeEventListener("keydown", this._onKeydown))
    }
  }

  switch(event) {
    event.preventDefault()
    const tab = event.currentTarget.dataset.tab
    this.activeTabValue = tab
    this.showTab(tab)
    this._writeRemembered(tab)
  }

  handleKeydown(event) {
    const tabs = this.tabTargets
    const currentIndex = tabs.indexOf(event.currentTarget)
    let newIndex = currentIndex

    switch (event.key) {
      case "ArrowLeft":
        newIndex = currentIndex === 0 ? tabs.length - 1 : currentIndex - 1
        break
      case "ArrowRight":
        newIndex = currentIndex === tabs.length - 1 ? 0 : currentIndex + 1
        break
      case "Home":
        newIndex = 0
        break
      case "End":
        newIndex = tabs.length - 1
        break
      default:
        return
    }

    event.preventDefault()
    const targetTab = tabs[newIndex]
    this.activeTabValue = targetTab.dataset.tab
    this.showTab(targetTab.dataset.tab)
    this._writeRemembered(targetTab.dataset.tab)
    targetTab.focus()
  }

  showTab(name) {
    this.tabTargets.forEach(tab => {
      const isActive = tab.dataset.tab === name
      tab.classList.toggle("border-primary-600", isActive)
      tab.classList.toggle("text-primary-600", isActive)
      tab.classList.toggle("border-transparent", !isActive)
      tab.classList.toggle("text-neutral-500", !isActive)
      tab.setAttribute("aria-selected", isActive ? "true" : "false")
      tab.setAttribute("tabindex", isActive ? "0" : "-1")
    })

    this.panelTargets.forEach(panel => {
      panel.classList.toggle("hidden", panel.dataset.tab !== name)
    })
  }

  // localStorage helpers — keyed by pathname so each client / page gets
  // its own remembered tab. Wrapped in try/catch because localStorage
  // can throw in private browsing / quota-exceeded states.
  _storageKey() {
    return `tabs:${window.location.pathname}`
  }

  _readRemembered() {
    try {
      return localStorage.getItem(this._storageKey())
    } catch (_e) {
      return null
    }
  }

  _writeRemembered(tab) {
    try {
      localStorage.setItem(this._storageKey(), tab)
    } catch (_e) {
      // ignore — falls back to default-tab behavior next visit
    }
  }

  _isValidTab(name) {
    return this.tabTargets.some(t => t.dataset.tab === name)
  }
}
